<script lang="ts" setup>
import RequisitionFilter from "./RequisitionFilter.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { useRequisitionStore } from "@/stores/management/requisition";
import { toast } from "vue-sonner";
import { formatDateTime, getBadgeClassStatusRequisition, getBadgeClassUrgencyRequisition } from "@/utils/helper";

const headers: Header[] = [
  { text: "DATE", value: "date" },
  { text: "PIC ONBOARD", value: "pic_onboard" },
  { text: "STATUS", value: "requisition_status" },
  { text: "REF NO", value: "ref_no" },
  { text: "PORT", value: "port_name" },
  { text: "CATEGORY", value: "category_name" },
  { text: "GROUP CATEGORY", value: "group_category_name" },
  { text: "PRIORITY", value: "priority" },
  { text: "ACTIONS", value: "action" },
];

const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listRequisition = ref<any>([]);
const isLoading = ref(false);
const storeRequisition = useRequisitionStore();

const loadDataRequisition = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await storeRequisition.dispatchGetListIncoming(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listRequisition.value = await storeRequisition.requisitions.items;
    page.value = await storeRequisition.requisitions.page;
    limit.value = await storeRequisition.requisitions.pageSize;
    totalItems.value = await storeRequisition.requisitions.total;
    isLoading.value = false;
  }
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataRequisition();
};

const handleEdit = (requisition: any) => {
  router.push("/management/requisition/" + requisition);
};

const handleDetail = (requisition: any) => {
  router.push("/management/requisition/detail/" + requisition);
};

const handleAddItem = (requisition: any) => {
  router.push("/management/requisition/add/" + requisition);
};

const handleDelete = async (requisition: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${requisition.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await storeRequisition.dispatchDelete(
          requisition.id
        );
        if (success) {
          toast.error("Your requisition has been deleted");
          loadDataRequisition();
        } else {
          toast.error(message);
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};
loadDataRequisition();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Incoming Requisition</h3>
      </div>
      <RequisitionFilter @searchByQuery="loadDataRequisition" :statusFilter=false />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listRequisition"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-date="props">
            {{ formatDateTime(props.created_at) }}
          </template>
          <template #item-requisition_status="props">
            <span
              :class="getBadgeClassStatusRequisition(props.requisition_status)"
            >
              {{ props.requisition_status }}
            </span>
          </template>
          <template #item-priority="props">
            <span
              :class="getBadgeClassUrgencyRequisition(props.priority)"
            >
              {{ props.priority }}
            </span>
          </template>
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
                v-if="props.requisition_status.toLocaleLowerCase() == 'draft'"
              >
                Edit
              </button>
              <button
                class="btn btn-primary btn-sm text-white"
                @click="handleDetail(props.id)"
              >
                View
              </button>
              <button
                class="btn btn-success btn-sm text-white"
                @click="handleAddItem(props.id)"
                v-if="props.requisition_status.toLocaleLowerCase() == 'draft'"
              >
                Item
              </button>
              <button
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
